import React, { useState, useMemo, useRef } from 'react';
import { textToAoa, readWorkbook, sheetToAoa, pickBestSheet, aoaToTable } from '../components/tabularImportUtils';

const ACCENT = '#f472b6';

// Canonical minimum structure (always offered, in this order) + two routing
// options: keep an unmapped column as an "extra", or ignore it entirely.
const CANONICAL = [
  { key:'itemNumber',            label:'Item No.' },
  { key:'ninPartNumber',         label:'Part Number (NIN P/N)' },
  { key:'description',           label:'Description' },
  { key:'quantity',              label:'Qty.' },
  { key:'unit',                  label:'Unit' },
  { key:'manufacturerPartNumber',label:'MFG P/N' },
  { key:'manufacturer',          label:'MFG' },
  { key:'reference',             label:'Reference' },
];
const CANONICAL_KEYS = CANONICAL.map(f => f.key);
const FIELDS = [
  ...CANONICAL,
  { key:'__extra',  label:'Keep as extra' },
  { key:'__ignore', label:'Ignore' },
];

// Heuristic header → field guesses for a friendlier first pass.
// Returns a canonical key, or '' when no canonical field fits (→ kept as extra).
function guessField(header) {
  const h = String(header || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!h) return '';
  if (/(itemno|itemnumber|^item$|^pos$|^line$|^no$|^nr$)/.test(h)) return 'itemNumber';
  // MFG P/N must be detected BEFORE plain manufacturer (MFG).
  if (/(manufacturerpart|mfgpart|mfgpn|mfgpno|mfgp|mpn|mfrpart|mfrpn)/.test(h)) return 'manufacturerPartNumber';
  if (/(manufacturer|^mfg$|^mfr$|maker|brand|vendor)/.test(h)) return 'manufacturer';
  if (/(ninpn|ninpart|^pn$|^part$|partnumber|partno|^nin)/.test(h)) return 'ninPartNumber';
  if (/(desc|description|name|component)/.test(h)) return 'description';
  if (/(qty|quantity|amount|count)/.test(h)) return 'quantity';
  if (/(unit|uom|measure)/.test(h)) return 'unit';
  if (/(ref|reference|designator|refdes)/.test(h)) return 'reference';
  return '';
}

// Build canonical-structure items (always all 8 fields, blank if unmapped) plus
// an `attributes` list carrying every column mapped to "Keep as extra". Pure —
// module-level so it isn't a hook dependency.
function buildItems(mapObj, p) {
  const idxByField = {};
  const extraCols = [];   // [{ col, header }]
  Object.entries(mapObj).forEach(([col, field]) => {
    if (CANONICAL_KEYS.includes(field)) idxByField[field] = Number(col);
    else if (field === '__extra') extraCols.push({ col: Number(col), header: p.headers[Number(col)] || `Column ${Number(col) + 1}` });
  });
  return p.rows
    .map(r => {
      const get = f => (idxByField[f] != null ? (r[idxByField[f]] || '').trim() : '');
      const hasQty = idxByField.quantity != null;
      const qty = parseFloat(get('quantity').replace(',', '.'));
      const attributes = extraCols
        .map(({ col, header }) => ({ key: header, value: (r[col] || '').trim() }))
        .filter(a => a.value !== '');
      return {
        itemNumber: get('itemNumber'),
        ninPartNumber: get('ninPartNumber'),
        description: get('description'),
        quantity: hasQty && Number.isFinite(qty) ? qty : null,
        unit: get('unit') || 'pcs',
        manufacturerPartNumber: get('manufacturerPartNumber'),
        manufacturer: get('manufacturer'),
        reference: get('reference'),
        notes: '',
        attributes,
      };
    })
    .filter(it => it.itemNumber || it.ninPartNumber || it.description);
}

/**
 * BomImport — paste / CSV / .xlsx upload + column mapping + preview.
 * Calls onItems(items[]) whenever the mapped, previewable item list changes.
 */
export default function BomImport({ onItems, onFileName }) {
  const [raw, setRaw] = useState('');
  const [aoa, setAoa] = useState([]);           // raw rows (pre-header-detection)
  const [headerRow, setHeaderRow] = useState(0);// chosen header row index
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});   // columnIndex -> field key
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [wb, setWb] = useState(null);           // parsed workbook (for multi-sheet)
  const [sheetNames, setSheetNames] = useState([]);
  const [sheet, setSheet] = useState('');
  const fileRef = useRef(null);

  const emit = (mapObj, p) => { if (onItems) onItems(buildItems(mapObj, p)); };

  // Apply a {headers,rows} table: auto-guess the column mapping + emit.
  const applyTable = (table) => {
    const map = {};
    // Columns that don't match a canonical field default to "keep as extra"
    // so no data is dropped silently.
    table.headers.forEach((h, i) => { map[i] = guessField(h) || '__extra'; });
    const p = { headers: table.headers, rows: table.rows };
    setParsed(p); setMapping(map);
    emit(map, p);
  };

  // Take freshly-parsed raw rows: auto-detect the header row, then apply.
  const ingestAoa = (rows, name) => {
    setAoa(rows);
    const table = aoaToTable(rows, null);   // null → auto-detect
    setHeaderRow(table.headerIndex);
    setFileName(name || '');
    if (onFileName) onFileName(name || '');
    applyTable(table);
  };

  // User overrides which row is the header — re-parse from the stored raw rows.
  const changeHeaderRow = (idx) => { setHeaderRow(idx); applyTable(aoaToTable(aoa, idx)); };

  // Switch to another sheet in the loaded workbook — re-detects its header row.
  const changeSheet = (name) => { setSheet(name); ingestAoa(sheetToAoa(wb, name), fileName); };

  const clearWorkbook = () => { setWb(null); setSheetNames([]); setSheet(''); };

  const onPaste = e => { clearWorkbook(); setRaw(e.target.value); ingestAoa(textToAoa(e.target.value), ''); };

  const onFile = file => {
    if (!file) return;
    const name = file.name || '';
    const reader = new FileReader();
    if (/\.(xlsx|xlsm|xls)$/i.test(name)) {
      // Binary workbook — parse with SheetJS; default to the most table-like sheet
      // (a multi-sheet workbook then offers a sheet picker). Clear the paste box.
      reader.onload = ev => {
        try {
          const { wb: workbook, sheetNames: names } = readWorkbook(ev.target.result);
          const chosen = pickBestSheet(workbook, names);
          setWb(workbook); setSheetNames(names); setSheet(chosen);
          ingestAoa(sheetToAoa(workbook, chosen), name); setRaw('');
        } catch { clearWorkbook(); setFileName(`Could not read ${name}`); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = ev => { const text = String(ev.target.result || ''); clearWorkbook(); setRaw(text); ingestAoa(textToAoa(text), name); };
      reader.readAsText(file);
    }
  };

  const setColMap = (col, field) => {
    const next = { ...mapping };
    // A CANONICAL field maps to at most one column — bump any prior holder back
    // to "extra". __extra/__ignore may repeat freely.
    if (CANONICAL_KEYS.includes(field)) {
      Object.keys(next).forEach(c => { if (next[c] === field && Number(c) !== Number(col)) next[c] = '__extra'; });
    }
    next[col] = field;
    setMapping(next);
    emit(next, parsed);
  };

  const previewItems = useMemo(() => buildItems(mapping, parsed), [mapping, parsed]);

  const hasPn = Object.values(mapping).includes('ninPartNumber');
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };
  const ta = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'10px 12px',fontSize:12,width:'100%',
    boxSizing:'border-box',fontFamily:'monospace',resize:'vertical' };
  const sel = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'5px 8px',fontSize:12,width:'100%',boxSizing:'border-box' };

  return (
    <div>
      {/* Drag & drop / file picker */}
      <div
        onDragOver={e=>{ e.preventDefault(); setDragOver(true); }}
        onDragLeave={()=>setDragOver(false)}
        onDrop={e=>{ e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files?.[0]); }}
        onClick={()=>fileRef.current && fileRef.current.click()}
        style={{ border:`2px dashed ${dragOver?ACCENT:'var(--ni-border-soft)'}`,borderRadius:10,
          padding:'18px',textAlign:'center',cursor:'pointer',marginBottom:14,
          background:dragOver?ACCENT+'11':'transparent',transition:'all 0.15s' }}>
        <div style={{ fontSize:13,color:'var(--ni-text)',fontWeight:600 }}>
          Drop an Excel (.xlsx) or .csv file here, or click to choose
        </div>
        <div style={{ fontSize:12,color:'var(--ni-text-muted)',marginTop:4 }}>
          {fileName ? `Loaded: ${fileName}` : 'The file is read locally in your browser — nothing is uploaded until you save'}
        </div>
        <input ref={fileRef} type="file"
          accept=".xlsx,.xlsm,.xls,.csv,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={e=>onFile(e.target.files?.[0])} style={{ display:'none' }} />
      </div>

      <div style={{ textAlign:'center',color:'var(--ni-text-muted)',fontSize:11,margin:'0 0 12px',
        textTransform:'uppercase',letterSpacing:'0.08em' }}>— or paste from Excel —</div>

      <div style={{ marginBottom:16 }}>
        <label style={labelS}>Paste rows (tab-separated from Excel, or CSV)</label>
        <textarea value={raw} onChange={onPaste} rows={6}
          placeholder={'NIN P/N\tDescription\tQty\tUnit\nNIN-1001\tResistor 10k 0603\t4\tpcs'}
          style={ta} />
      </div>

      {sheetNames.length > 1 && (
        <div style={{ marginBottom:12,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap' }}>
          <label style={{ ...labelS,margin:0 }}>Sheet</label>
          <select value={sheet} onChange={e=>changeSheet(e.target.value)}
            style={{ ...sel,width:'auto',minWidth:200,maxWidth:'100%' }}>
            {sheetNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ fontSize:11,color:'var(--ni-text-muted)' }}>
            This workbook has {sheetNames.length} sheets — pick the one to import.
          </span>
        </div>
      )}

      {aoa.length > 0 && (
        <div style={{ marginBottom:12,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap' }}>
          <label style={{ ...labelS,margin:0 }}>Header row</label>
          <select value={headerRow} onChange={e=>changeHeaderRow(Number(e.target.value))}
            style={{ ...sel,width:'auto',minWidth:260,maxWidth:'100%' }}>
            {aoa.slice(0, 30).map((r, i) => (
              <option key={i} value={i}>Row {i + 1}: {(r.filter(c=>c!=='').slice(0,4).join(' | ') || '(empty)').slice(0,60)}</option>
            ))}
          </select>
          <span style={{ fontSize:11,color:'var(--ni-text-muted)' }}>
            Auto-detected the column titles. Change this if a title/legend row was picked instead.
          </span>
        </div>
      )}

      {parsed.headers.length > 0 && (
        <>
          <div style={{ marginBottom:6 }}>
            <label style={labelS}>Map columns</label>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(170px, 1fr))',gap:10,marginBottom:8 }}>
            {parsed.headers.map((h, i) => (
              <div key={i} style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
                borderRadius:8,padding:'8px 10px' }}>
                <div style={{ fontSize:11,color:'var(--ni-text-muted)',marginBottom:5,whiteSpace:'nowrap',
                  overflow:'hidden',textOverflow:'ellipsis' }} title={h}>{h}</div>
                <select value={mapping[i] || ''} onChange={e=>setColMap(i, e.target.value)} style={sel}>
                  {FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>
            ))}
          </div>
          {!hasPn && (
            <div style={{ color:'#f59e0b',fontSize:12,marginBottom:12 }}>
              Map a column to <strong>NIN P/N</strong> so items can be matched to stations.
            </div>
          )}

          <div style={{ marginBottom:6 }}>
            <label style={labelS}>Preview — {previewItems.length} item{previewItems.length!==1?'s':''}</label>
          </div>
          <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
            borderRadius:10,overflow:'auto',maxHeight:320 }}>
            <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
              <thead>
                <tr style={{ color:'var(--ni-text-muted)',fontSize:11,textTransform:'uppercase',
                  letterSpacing:'0.06em',background:'rgba(0,0,0,0.2)',position:'sticky',top:0 }}>
                  {['Item No.','Part Number','Description','Qty','Unit','MFG P/N','MFG','Extras'].map(h=>(
                    <th key={h} style={{ padding:'8px 12px',textAlign:'left',fontWeight:600,whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewItems.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding:24,textAlign:'center',color:'var(--ni-text-muted)' }}>
                    No items yet — paste data or map a part-number / description column.
                  </td></tr>
                ) : previewItems.slice(0, 200).map((it, i) => (
                  <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                    <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>{it.itemNumber||'—'}</td>
                    <td style={{ padding:'7px 12px',fontFamily:'monospace',color:ACCENT,whiteSpace:'nowrap' }}>{it.ninPartNumber||'—'}</td>
                    <td style={{ padding:'7px 12px' }}>{it.description||'—'}</td>
                    <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)' }}>{it.quantity==null?'—':it.quantity}</td>
                    <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)' }}>{it.unit}</td>
                    <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)',fontFamily:'monospace' }}>{it.manufacturerPartNumber||'—'}</td>
                    <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)' }}>{it.manufacturer||'—'}</td>
                    <td style={{ padding:'7px 12px',color:'var(--ni-text-muted)',fontSize:11 }} title={(it.attributes||[]).map(a=>`${a.key}: ${a.value}`).join(' · ')}>
                      {(it.attributes||[]).length ? `${it.attributes.length} extra${it.attributes.length!==1?'s':''}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {previewItems.length > 200 && (
            <div style={{ marginTop:6,color:'var(--ni-text-muted)',fontSize:11 }}>
              Showing first 200 of {previewItems.length} rows. All will be saved.
            </div>
          )}
        </>
      )}
    </div>
  );
}
